/**
 * When this account's password expires — said permanently, on the account page.
 *
 * `PasswordNotice` is the other half of this and deliberately says nothing
 * until the date is close: a banner across every screen announcing something
 * eleven weeks away is noise, and noise is what makes the urgent one invisible.
 *
 * This is the opposite case. Somebody who has opened "Your account" is asking
 * about their account, and "when does my password expire?" is one of the two
 * questions that page exists to answer. So it always answers, and changes tone
 * as the date approaches rather than appearing from nowhere.
 *
 * It answers for every role (17 Sep 2026), which for an admin means saying that
 * the password does not expire. That is a real answer and the one an admin
 * needs: the alternative — a blank space where everyone else sees a date —
 * reads as a page that failed to load.
 */

import {
  PASSWORD_MAX_AGE_DAYS,
  isUrgent,
  shouldWarn,
} from '@parinaam-vms/shared';
import type { PasswordStatus } from '@parinaam-vms/shared';
import styles from './PasswordExpiry.module.css';

interface Props {
  status: PasswordStatus;
}

/**
 * The date as a person reads it.
 *
 * `en-IN` with a long month: "11 January 2027" cannot be misread the way
 * 11/01/2027 can by anyone who has ever used an American system. The same
 * formatter as `PasswordNotice`, for the same reason.
 */
function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso));
}

/** "in 97 days", and the three cases where counting days reads badly. */
function howLong(days: number): string {
  if (days < 0) return 'It has expired.';
  if (days === 0) return 'That is today.';
  if (days === 1) return 'That is tomorrow.';
  return `That is ${days} days from now.`;
}

export function PasswordExpiry({ status }: Props) {
  // An account whose password never expires. The admin case, and the reason
  // this component takes a status rather than a role: the rule lives in
  // `password-policy.ts`, and a component that re-derived it from the role
  // would be a second copy of it to keep in step.
  if (status.expires_at === null || status.days_remaining === null) {
    return (
      <div className={`${styles.panel} ${styles.neutral}`} role="status">
        <p className={styles.headline}>Your password does not expire.</p>
        <p className={styles.detail}>
          Administrator passwords do not expire.
        </p>
      </div>
    );
  }

  const urgent = isUrgent(status);
  const tone = urgent ? styles.urgent : shouldWarn(status) ? styles.soon : styles.neutral;

  return (
    <div
      className={`${styles.panel} ${tone}`}
      // Announced the moment it appears only when it is urgent. A date three
      // months out is a fact on a page, not something to interrupt anybody for.
      role={urgent ? 'alert' : 'status'}
    >
      <p className={styles.headline}>
        Your password expires on {formatDate(status.expires_at)}.
      </p>
      <p className={styles.detail}>
        {howLong(status.days_remaining)} Passwords last {PASSWORD_MAX_AGE_DAYS} days, and
        changing it below starts the count again.
      </p>
    </div>
  );
}
